import {
  MPUserIdentities,
  mParticleInit,
  mParticleLogEvent,
  mParticleEventType,
  MPInitOptions,
} from '@treatwell/ui';
import {
  growthbookInitialiseClient,
  getDeviceId,
} from '@treatwell/growthbook-js/client';
import {configPromise} from './config';
import {Environment, GrowthbookConfig} from '../shared/types';
import {fetchChannel} from './api/rainbow';
import {fetchUser} from './api/mp-api';
import type {GrowthBookAttributes} from '@treatwell/growthbook-js';
import type {GrowthBook, Context} from '@growthbook/growthbook';
import type {Channel} from './types/account-api/ChannelOutput';
import type {MpApiMeResponse} from './store/models/user/types';
import {initialiseDatadog} from '@treatwell/ui/datadog';
import {
  configureTolgee,
  fetchTranslation,
  getLocale,
  initI18next,
} from '@treatwell/tolgee/client';
import tolgeeConfig from '../tolgee.config.json';
import {getFallbackLocaleLoader} from './utils/getFallbackLocaleLoader';
import {fetchLocale} from './utils/datetime';
import {handleLocaleError} from './utils/handleLocaleError';

let growthbookResolve: (value: GrowthBook) => void;
const growthbookPromise = new Promise<GrowthBook>((resolve) => {
  growthbookResolve = resolve;
});

export function getGrowthbook() {
  return growthbookPromise;
}

export async function initialiseCoreLibraries() {
  const [channel, user, config] = await Promise.allSettled([
    fetchChannel(),
    fetchUser(),
    configPromise,
  ]);

  let environment: Environment = 'dev';
  let growthbookConfig: GrowthbookConfig[Environment] = {
    clientKey: '',
    apiHost: '',
  };
  if (config.status === 'fulfilled') {
    environment = config.value.environment;
    growthbookConfig = config.value.growthbook;
    const datadogEnv = config.value.twEnvName || environment;
    const version = config.value.version;

    initialiseDatadog({
      config: {
        applicationId: 'c15d2fd1-d37e-468b-9ebc-3bcac3307b10',
        clientToken: 'pub392c208e67d3e60dfe3151d61804300a',
        site: 'datadoghq.eu',
        service: 'account-ui',
        version: version,
        env: datadogEnv,
      },
    });
  }

  configureTolgee({
    ...tolgeeConfig,
    isEnvProduction: environment === 'prod',
    getFallbackLocaleLoader,
  });

  const {channelAttributes, userAttributes, userIdentities} = extractAttributes(
    channel,
    user
  );
  const channelLocale =
    channel.status === 'fulfilled' ? channel.value.locale : 'en';
  const locale = getLocale(channelLocale);

  await Promise.all([
    fetchTranslation(locale)
      .then((translations) => initI18next(locale, translations))
      .catch((error) => {
        handleLocaleError(error);
      }),
    fetchLocale(channelLocale),
  ]);

  const deviceId = await getDeviceId();
  initialiseMParticle(environment, userIdentities, deviceId);

  const growthbook = await growthbookInitialiseClient(
    getGrowthBookConfig(environment, growthbookConfig),
    deviceId,
    {
      growthbookAttributes: {
        channel: channelAttributes,
        user: userAttributes,
      },
      activationEvent: {
        type: 'mparticle',
        send(defaultAttributes) {
          mParticleLogEvent(
            '$experiment_started',
            mParticleEventType.Other,
            defaultAttributes
          );
        },
      },
    }
  );

  growthbookResolve(growthbook);
}

function getGrowthBookConfig(
  environment: Environment,
  growthbookConfig: GrowthbookConfig[Environment]
) {
  const config: Context = {
    clientKey: growthbookConfig.clientKey,
    apiHost: growthbookConfig.apiHost,
  };
  if (environment !== 'prod') {
    config.enableDevMode = true;
    config.disableCache = true;
  }
  return config;
}

function extractAttributes(
  channel: PromiseSettledResult<Channel>,
  user: PromiseSettledResult<MpApiMeResponse>
) {
  const channelAttributes: GrowthBookAttributes['channel'] = {};
  const userAttributes: GrowthBookAttributes['user'] = {};
  const userIdentities: MPUserIdentities = {};

  if (channel.status === 'fulfilled') {
    channelAttributes.country = channel.value.country.countryCode;
  }

  if (user.status === 'fulfilled') {
    const {accountId, email} = user.value;
    userAttributes.account_id = String(accountId);
    userAttributes.is_authenticated = true;
    userIdentities.customerid = `${accountId}`;
    userIdentities.email = email;
  } else {
    userAttributes.is_authenticated = false;
  }

  return {channelAttributes, userAttributes, userIdentities};
}

async function initialiseMParticle(
  environment: Environment,
  userIdentities: MPUserIdentities,
  deviceId: string
) {
  try {
    // Download the mParticle SDK before the kits, since the kits depend on it
    const mParticle = await import(
      /* webpackChunkName: "mparticle_web-sdk" */ '@mparticle/web-sdk'
    );
    const [onetrustKit, brazeKit] = await Promise.all([
      import(
        /* webpackChunkName: "mparticle_web-onetrust-kit" */ '@mparticle/web-onetrust-kit'
      ),
      import(
        /* webpackChunkName: "mparticle_web-braze-kit" */ '@mparticle/web-braze-kit'
      ),
    ]);

    const options: MPInitOptions = {
      isDevelopmentMode: environment !== 'prod',
      deviceId,
    };

    // Register kits before initializing mParticle,
    // so that when mParticle starts, it initialises the kits automatically
    onetrustKit.register(options);
    brazeKit.default.register(options);

    mParticleInit(mParticle.default, options, userIdentities);
  } catch (error) {
    console.error('Failed to initialize mParticle:', error);
  }
}
